import React, { useEffect, useState, useRef } from "react";

import "./styles.css";
import { Page, Spinner } from "../../components";
import { useAdminGetAgencies, useAdminGetListedAgencies, useAdminSearchUsers } from "../../api/hooks/admin";
import { useNavigate } from "react-router-dom";
import { Agency, Dict, UserSearchResult } from "@/api/admin/types";
import CreateAgencyModal from "../../components/CreateAgencyModal";
import LogoutButton from "../../components/LogoutButton";
import { escapeCsvField } from "../../utils/csv";

const baseColumns: { key: keyof Agency; label: string }[] = [
  { key: "name", label: "Agency Name" },
];

const extendedColumns: { key: keyof Dict; label: string }[] = [
  { key: "name", label: "Agency Name" },
  { key: "agencyId", label: "Agency ID" },
  { key: "userCount", label: "User Count" },
  { key: "creationDate", label: "Account Created" },
  { key: "policiesCount", label: "Policies This Week" },
  { key: "comparisonsCount", label: "Comparison This Week" },
  { key: "proposalsCount", label: "Proposals This Week" },
  { key: "contractReviewsCount", label: "Contract Review This Week" },
  { key: "gapAnalysisCount", label: "Coverage Gap Analysis This Week" },
  { key: "singlePolicyAnalysisCount", label: "Single Policy Analysis This Week" },
  { key: "policiesCountLastWeek", label: "Policies Last Week" },
  { key: "comparisonsCountLastWeek", label: "Comparison Last Week" },
  { key: "proposalsCountLastWeek", label: "Proposals Last Week" },
  { key: "contractReviewsCountLastWeek", label: "Contract Review Last Week" },
  { key: "gapAnalysisCountLastWeek", label: "Coverage Gap Analysis Last Week" },
  { key: "singlePolicyAnalysisCountLastWeek", label: "Single Policy Analysis Last Week" },
];

type BaseSortKey = keyof Agency | "";
type ExtendedSortKey = keyof Dict | "";
type SortKey = BaseSortKey | ExtendedSortKey;
type SortOrder = "asc" | "desc";

function arrayToCSV(data: any[], columns: { key: string; label: string }[]) {
  const header = columns.map(col => `"${col.label}"`).join(",");
  const rows = data.map(row =>
    columns.map(col => escapeCsvField(row[col.key])).join(",")
  );
  return [header, ...rows].join("\r\n");
}

function downloadCSV(csv: string, filename: string) {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

const AgencyListPage: React.FC = () => {
  const [localUsage, setLocalUsage] = useState<Agency[]>([]);
  const [search, setSearch] = useState<string>("");
  const [extendedTable, setExtendedTable] = useState<boolean>(false);
  const [sortKey, setSortKey] = useState<SortKey>("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [extendedUsage, setExtendedUsage] = useState<Dict[]>([]);
  const [isLoadingExtendedUsage, setIsLoadingExtendedUsage] = useState(false);
  const [showCreateAgencyModal, setShowCreateAgencyModal] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [showSelfServe, setShowSelfServe] = useState(false);
  const { data: agencies, isLoading: isLoadingAgencies } =
    useAdminGetListedAgencies(showInactive);
  // Full unfiltered list (same cached query the hook wraps) so self-serve
  // signups can be pulled into their own group regardless of the toggle.
  const { data: allAgencies } = useAdminGetAgencies();
  const query = search.trim().toLowerCase();
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);
  const {
    data: matchedUsers,
    isFetching,
    isError: userSearchFailed,
  } = useAdminSearchUsers(debouncedQuery);
  // Also true during the debounce window, so "no results" doesn't flash early.
  const isFetchingUsers = isFetching || (query.length >= 2 && debouncedQuery !== query);
  const extendedLoadedRef = useRef(false);
  const extendedUsageRef = useRef<Dict[]>([]);
  useEffect(() => {
    if (agencies) {
      const reversed = [...agencies].reverse();
      setLocalUsage(reversed);
    }
  }, [agencies]);

  useEffect(() => {
    if (extendedTable && !extendedLoadedRef.current) {
      setIsLoadingExtendedUsage(true);
      setExtendedUsage([]);
      extendedUsageRef.current = [];
      extendedLoadedRef.current = true;
      (async () => {
        try {
          const AdminController = require("../../controllers/AdminController").default;
          const gen = AdminController.getUsage();
          let count = 0;
          for await (const agency of gen) {
            if (!extendedUsageRef.current.some(a => a.agencyId === agency.agencyId)) {
              extendedUsageRef.current = [...extendedUsageRef.current, agency];
              setExtendedUsage([...extendedUsageRef.current].reverse());
              count++;
            }
          }
          console.log(`Loaded ${count} agencies for extended table`);
        } catch (error) {
          console.error("Failed to load extended usage data:", error);
        } finally {
          setIsLoadingExtendedUsage(false);
        }
      })();
    }
  }, [extendedTable]);

  const navigate = useNavigate();

  const goToAgency = (agencyId: string, name: string) => {
    navigate(`/agency/${agencyId}`, {
      state: { name },
    });
  };

  const renderAgencyCard = (
    agency: Agency,
    key: React.Key,
    { showInactiveBadge = true }: { showInactiveBadge?: boolean } = {}
  ) => (
    <div
      key={key}
      onClick={() => goToAgency(agency.agencyId, agency.name)}
      style={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: 8,
        padding: "16px 20px",
        cursor: "pointer",
        transition: "box-shadow 0.15s ease, border-color 0.15s ease",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 8,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#3949ab";
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(57,73,171,0.15)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#e0e0e0";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <span style={{ fontWeight: 500, fontSize: 14, color: "#1a1a1a" }}>
        {agency.name}
      </span>
      {matchedUsersByAgency.get(agency.agencyId)?.slice(0, 2).map((u) => (
        <span key={u.user_id} style={{ fontSize: 11, color: "#6b7280" }}>
          {u.name || u.email}
        </span>
      ))}
      {showInactiveBadge && !agency.isListed && (
        <span style={{ color: "#f44336", fontSize: 11, fontWeight: 600 }}>
          INACTIVE
        </span>
      )}
    </div>
  );

  const handleExtendTable = () => {
    setExtendedTable(true);
  };
  const handleHideExtendedTable = () => {
    setExtendedTable(false);
    setSortKey("");
    setSortOrder("asc");
    extendedLoadedRef.current = false;
  };

  const matchesSearch = (name?: string | null) =>
    !!name && name.toLowerCase().includes(query);

  // Users matching the debounced search (previous results are kept while the
  // next ones load), grouped by agency.
  const matchedUsersByAgency = new Map<string, UserSearchResult[]>();
  // keepPreviousData retains old results once the query is too short to fetch.
  for (const u of query.length >= 2 ? matchedUsers ?? [] : []) {
    if (!u.agency_id) continue;
    matchedUsersByAgency.set(u.agency_id, [...(matchedUsersByAgency.get(u.agency_id) ?? []), u]);
  }
  const matchesAgency = (agency: { name?: string | null; agencyId: string }) =>
    matchesSearch(agency.name) || matchedUsersByAgency.has(agency.agencyId);

  // The hook already applied the allowlist, so localUsage is what belongs on
  // screen. Keep self-serve signups out — they get their own section below.
  const filteredUsage = localUsage.filter(
    (agency) => !agency.isSelfServeSignup && matchesAgency(agency)
  );

  // Self-serve signups that never set a name. Their own collapsed section,
  // independent of the inactive toggle — they're signup leads, not agencies.
  const selfServeSignups = (allAgencies || []).filter(
    (agency) => agency.isSelfServeSignup && matchesAgency(agency)
  );

  // The extended table comes from a different endpoint that always streams
  // every agency, so scope it to the ids the hook handed us (minus self-serve).
  const visibleAgencyIds = new Set(
    localUsage
      .filter((agency) => !agency.isSelfServeSignup)
      .map((agency) => agency.agencyId)
  );

  const filteredExtendedUsage = (extendedUsage || []).filter(
    (agency: Dict) =>
      visibleAgencyIds.has(agency.agencyId) && matchesAgency(agency)
  );

  const getSortedData = () => {
    let data: any[] = [];
    if (!extendedTable) {
      data = filteredUsage;
    } else {
      data = filteredExtendedUsage;
    }
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
      }
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      return 0;
    });
  };

  const sortedUsage = getSortedData();

  const handleSort = (key: SortKey) => {
    if (!key) return;
    if (sortKey === key) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const handleExportCSV = async () => {
    let dataToExport = sortedUsage;
    try {
      const AdminController = require("../../controllers/AdminController").default;
      const exportData = await AdminController.getUsageExport();
      if (Array.isArray(exportData) && exportData.length > 0) {
        dataToExport = exportData;
      }
    } catch (e) {
      console.error("Failed to fetch export data, using current table data", e);
    }

    // Always export the extended columns so all fields are included
    const columnsToExport = extendedColumns;
    const csv = arrayToCSV(dataToExport, columnsToExport);
    downloadCSV(csv, "agencies_extended.csv");
  };

  return (
    <>
      <Page>
        {/* Header Section */}
        <div className="agency-list-page-header" style={{ 
          width: "100%", 
          maxWidth: "80vw", 
          marginBottom: 24, 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          padding: "0 16px",
          boxSizing: "border-box" as const,
          flexWrap: "wrap" as const,
          gap: 8,
        }}>
          <h1 style={{ 
            fontSize: "clamp(32px, 6vw, 49px)", 
            margin: 0,
            flex: "1 1 auto",
            minWidth: 0,
            fontWeight: 600,
            color: "#1a1a1a",
          }}>
            Choose your agency
          </h1>
          <div style={{ 
            flexShrink: 0, 
            display: "flex", 
            alignItems: "center", 
            gap: 12,
            flexWrap: "wrap" as const,
          }}>
            <button
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: "none",
                background: "#6b7280",
                color: "#fff",
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                transition: "background-color 0.2s ease",
              }}
              onClick={() => navigate("/")}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#4b5563";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#6b7280";
              }}
            >
              Back to Home
            </button>
            <LogoutButton />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ maxWidth: 400, width: "100%" }}>
            <input
              type="text"
              placeholder="Search by agency, user name or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: 4,
                border: "1px solid #ccc",
                fontSize: 16,
              }}
            />
            {(isFetchingUsers || userSearchFailed) && (
              <div style={{ fontSize: 12, marginTop: 4, color: userSearchFailed ? "#f44336" : "#6b7280" }}>
                {userSearchFailed ? "User search unavailable, showing agency names only" : "Searching users..."}
              </div>
            )}
          </div>
        </div>
        <div style={{ marginLeft: 0, marginTop: 0, display: "flex", gap: 16 }}>
          <button
            style={{
              padding: "10px 18px",
              borderRadius: 6,
              border: "none",
              background: "#1b3bcc",
              color: "#fff",
              fontSize: 16,
              cursor: "pointer",
              boxShadow: "0 2px 8px #0001"
            }}
            onClick={() => setShowCreateAgencyModal(true)}
          >
            Create new agency
          </button>
          {!extendedTable && (
            <button
              style={{
                padding: "10px 18px",
                borderRadius: 6,
                border: "none",
                background: "#1b3bcc",
                color: "#fff",
                fontSize: 16,
                cursor: "pointer",
                boxShadow: "0 2px 8px #0001"
              }}
              onClick={handleExtendTable}
            >
              Load extended table
            </button>
          )}
          {extendedTable && (
            <>
              <button
                style={{
                  padding: "10px 18px",
                  borderRadius: 6,
                  border: "none",
                  background: "#1b3bcc",
                  color: "#fff",
                  fontSize: 16,
                  cursor: "pointer",
                  boxShadow: "0 2px 8px #0001"
                }}
                onClick={handleHideExtendedTable}
              >
                Hide extended table
              </button>
              <button
                style={{
                  padding: "10px 18px",
                  borderRadius: 6,
                  border: "none",
                  background: "#10b981",
                  color: "#fff",
                  fontSize: 16,
                  cursor: "pointer",
                  boxShadow: "0 2px 8px #0001"
                }}
                onClick={handleExportCSV}
              >
                Export CSV
              </button>
            </>
          )}
          <button
            style={{
              padding: "10px 18px",
              borderRadius: 6,
              border: "none",
              background: "#f5a623",
              color: "#fff",
              fontSize: 16,
              cursor: "pointer",
              boxShadow: "0 2px 8px #0001"
            }}
            onClick={() => setShowInactive(v => !v)}
          >
            {showInactive ? "Hide inactive agencies" : "Show inactive agencies"}
          </button>
        </div>
        {!extendedTable ? (
          <div style={{ marginTop: 16, width: "100%", maxWidth: "80vw" }}>
            {sortedUsage.length === 0 && !isLoadingAgencies && !isFetchingUsers ? (
              <div style={{ color: "#888", fontSize: 18, marginTop: 32 }}>
                No agencies or users found for this search
              </div>
            ) : (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: 12,
              }}>
                {!isLoadingAgencies &&
                  sortedUsage.map((agency, index) => renderAgencyCard(agency, index))}
              </div>
            )}
            {!isLoadingAgencies && selfServeSignups.length > 0 && (
              <div style={{ marginTop: 28 }}>
                <button
                  onClick={() => setShowSelfServe((v) => !v)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: "none",
                    border: "none",
                    padding: "4px 0",
                    cursor: "pointer",
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#4b5563",
                  }}
                >
                  <span style={{ fontSize: 12 }}>{showSelfServe ? "▼" : "▶"}</span>
                  Self-serve signups ({selfServeSignups.length})
                </button>
                {showSelfServe && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                      gap: 12,
                      marginTop: 12,
                    }}
                  >
                    {selfServeSignups.map((agency, index) =>
                      renderAgencyCard(agency, `self-serve-${index}`, {
                        showInactiveBadge: false,
                      })
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div style={{ width: "100%", overflowX: "auto", marginTop: 16 }}>
            {sortedUsage.length === 0 && !isLoadingExtendedUsage && !isFetchingUsers ? (
              <div style={{ color: "#888", fontSize: 18, marginTop: 32 }}>
                No agencies or users found for this search
              </div>
            ) : (
              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 1400, fontSize: 13 }}>
                <thead>
                  <tr>
                    <th colSpan={4} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: 13, backgroundColor: "#3949ab", color: "#fff", borderRight: "2px solid rgba(255,255,255,0.3)" }}>Agency Info</th>
                    <th colSpan={6} style={{ padding: "8px 12px", textAlign: "center", fontWeight: 600, fontSize: 13, backgroundColor: "#3949ab", color: "#fff", borderRight: "2px solid rgba(255,255,255,0.3)" }}>This Week</th>
                    <th colSpan={6} style={{ padding: "8px 12px", textAlign: "center", fontWeight: 600, fontSize: 13, backgroundColor: "#3949ab", color: "#fff" }}>Last Week</th>
                  </tr>
                  {(() => {
                    const sh: React.CSSProperties = { padding: "6px 10px", textAlign: "center", fontWeight: 500, fontSize: 12, backgroundColor: "#5c6bc0", color: "#fff", whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" };
                    const sa = (key: keyof Dict) => sortKey === key ? (sortOrder === "asc" ? " \u25B2" : " \u25BC") : "";
                    const gb: React.CSSProperties = { borderRight: "2px solid rgba(255,255,255,0.3)" };
                    return (
                      <tr>
                        <th style={{ ...sh, textAlign: "left", minWidth: 150 }} onClick={() => handleSort("name")}>Name{sa("name")}</th>
                        <th style={{ ...sh, minWidth: 80 }} onClick={() => handleSort("agencyId")}>ID{sa("agencyId")}</th>
                        <th style={{ ...sh, minWidth: 55 }} onClick={() => handleSort("userCount")}>Users{sa("userCount")}</th>
                        <th style={{ ...sh, minWidth: 90, ...gb }} onClick={() => handleSort("creationDate")}>Created{sa("creationDate")}</th>
                        <th style={{ ...sh, minWidth: 65 }} onClick={() => handleSort("policiesCount")}>Policies{sa("policiesCount")}</th>
                        <th style={{ ...sh, minWidth: 60 }} onClick={() => handleSort("comparisonsCount")}>Comps{sa("comparisonsCount")}</th>
                        <th style={{ ...sh, minWidth: 70 }} onClick={() => handleSort("proposalsCount")}>Proposals{sa("proposalsCount")}</th>
                        <th style={{ ...sh, minWidth: 65 }} onClick={() => handleSort("contractReviewsCount")}>Reviews{sa("contractReviewsCount")}</th>
                        <th style={{ ...sh, minWidth: 45 }} onClick={() => handleSort("gapAnalysisCount")}>Gap{sa("gapAnalysisCount")}</th>
                        <th style={{ ...sh, minWidth: 45, ...gb }} onClick={() => handleSort("singlePolicyAnalysisCount")}>SPA{sa("singlePolicyAnalysisCount")}</th>
                        <th style={{ ...sh, minWidth: 65 }} onClick={() => handleSort("policiesCountLastWeek")}>Policies{sa("policiesCountLastWeek")}</th>
                        <th style={{ ...sh, minWidth: 60 }} onClick={() => handleSort("comparisonsCountLastWeek")}>Comps{sa("comparisonsCountLastWeek")}</th>
                        <th style={{ ...sh, minWidth: 70 }} onClick={() => handleSort("proposalsCountLastWeek")}>Proposals{sa("proposalsCountLastWeek")}</th>
                        <th style={{ ...sh, minWidth: 65 }} onClick={() => handleSort("contractReviewsCountLastWeek")}>Reviews{sa("contractReviewsCountLastWeek")}</th>
                        <th style={{ ...sh, minWidth: 45 }} onClick={() => handleSort("gapAnalysisCountLastWeek")}>Gap{sa("gapAnalysisCountLastWeek")}</th>
                        <th style={{ ...sh, minWidth: 45 }} onClick={() => handleSort("singlePolicyAnalysisCountLastWeek")}>SPA{sa("singlePolicyAnalysisCountLastWeek")}</th>
                      </tr>
                    );
                  })()}
                </thead>
                <tbody>
                  {sortedUsage.map((agency: Dict, index: number) => {
                      const baseAgency = localUsage.find((a) => a.agencyId === agency.agencyId);
                      const isListed = baseAgency ? baseAgency.isListed : true;
                      const c: React.CSSProperties = { padding: "8px 10px", textAlign: "center", borderBottom: "1px solid #e8e8e8" };
                      const gb: React.CSSProperties = { borderRight: "2px solid #e0e0e0" };
                      return (
                      <tr
                        onClick={() => goToAgency(agency.agencyId, agency.name)}
                        key={index}
                        style={{ backgroundColor: index % 2 === 0 ? "#f8f9ff" : "#fff", cursor: "pointer" }}
                      >
                        <td style={{ ...c, textAlign: "left", fontWeight: 500, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={agency.name}>
                          {agency.name}
                          {!isListed && (
                            <span style={{ color: "#f44336", marginLeft: 6, fontWeight: 500, fontSize: 11 }}>
                              (INACTIVE)
                            </span>
                          )}
                        </td>
                        <td style={{ ...c, fontSize: 11, fontFamily: "monospace", color: "#666" }} title={agency.agencyId}>{agency.agencyId.slice(0, 8)}</td>
                        <td style={c}>{agency.userCount}</td>
                        <td style={{ ...c, whiteSpace: "nowrap", ...gb }}>{agency.creationDate ? new Date(agency.creationDate).toLocaleDateString() : "\u2014"}</td>
                        <td style={c}>{agency.policiesCount}</td>
                        <td style={c}>{agency.comparisonsCount}</td>
                        <td style={c}>{agency.proposalsCount}</td>
                        <td style={c}>{agency.contractReviewsCount}</td>
                        <td style={c}>{agency.gapAnalysisCount}</td>
                        <td style={{ ...c, ...gb }}>{agency.singlePolicyAnalysisCount ?? 0}</td>
                        <td style={c}>{agency.policiesCountLastWeek}</td>
                        <td style={c}>{agency.comparisonsCountLastWeek}</td>
                        <td style={c}>{agency.proposalsCountLastWeek}</td>
                        <td style={c}>{agency.contractReviewsCountLastWeek}</td>
                        <td style={c}>{agency.gapAnalysisCountLastWeek}</td>
                        <td style={c}>{agency.singlePolicyAnalysisCountLastWeek ?? 0}</td>
                      </tr>
                    );
                    })}
                  {isLoadingExtendedUsage && (
                    <tr>
                      <td colSpan={16} style={{ textAlign: "center", padding: 24 }}>
                        <Spinner />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
        {(isLoadingAgencies && !extendedTable) && (
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <div style={{ fontSize: 18, color: "#1b3bcc", marginBottom: 8 }}>
              Loading agencies list...
            </div>
            <Spinner />
          </div>
        )}
        <CreateAgencyModal
          open={showCreateAgencyModal}
          onCancel={() => setShowCreateAgencyModal(false)}
          onCreate={() => setShowCreateAgencyModal(false)}
        />
      </Page>
    </>
  );
}

export default AgencyListPage;
